import { useEffect, useRef, useState } from 'react';
import { injectContentsquareScript } from '@contentsquare/tag-sdk';
import { readConsent, subscribeConsent, type CookieConsent } from '@/lib/cookieConsent';

/**
 * LA MESURE D'AUDIENCE — « Hotjar », c'est-à-dire le tag Contentsquare.
 *
 * CE QUI EST INSTALLÉ, ET POURQUOI CE N'EST PAS LE SNIPPET HOTJAR. La demande
 * parlait de Hotjar ; l'identifiant fourni (`7c211554d459f`) est un **tag ID
 * Contentsquare**, une chaîne hexadécimale, pas un `hjid` numérique. Hotjar
 * appartient à Contentsquare et les comptes sont servis par un tag unique,
 * `t.contentsquare.net/uxa/<tag>.js`, posé par `@contentsquare/tag-sdk`. Le
 * paquet `@hotjar/browser` attend un entier : il n'aurait rien chargé du tout.
 *
 * POURQUOI CE N'EST PAS LE WIZARD QUI A ÉCRIT CE FICHIER. `npx
 * @contentsquare/wizard install` pose `injectContentsquareScript()` dans
 * `main.tsx`, juste avant `createRoot` — donc pour TOUT visiteur, avant tout
 * consentement. C'est exactement la faute que `lib/cookieConsent.ts` a été
 * écrit pour fermer (Intercom démarrait pour tout le monde pendant que
 * `/privacy` promettait le contraire en trois langues). Le SDK et l'appel sont
 * ceux du wizard ; seul le MOMENT change, et ce moment est tout le sujet.
 *
 * LE RETRAIT DU CONSENTEMENT, QUI EST LA PARTIE DIFFICILE. Un script de
 * captation ne se décharge pas : une fois `uxa.js` évalué, il observe le DOM.
 * Contentsquare fournit pour ça `_uxa.push(['optout'])`, qui arrête l'envoi ET
 * purge ses propres cookies — c'est la seule façon honnête de reprendre ce
 * qu'on a démarré, et c'est plus que ce que Hotjar seul offrait.
 *
 * LA RÉCIPROQUE N'EXISTE PAS. La documentation Contentsquare ne publie AUCUNE
 * commande `optin` : `optout` pose un cookie `_cs_optout` valable 13 mois, et
 * rien ne le retire côté tag. Un visiteur qui refuse puis change d'avis
 * resterait donc exclu sans qu'on le lui dise. On efface le cookie nous-mêmes
 * (il est first-party, donc à notre portée) avant chaque démarrage. Dans la
 * même session, après un `optout`, la collecte ne reprend qu'au prochain
 * chargement complet de la page : ce fichier ne prétend pas mieux, faute de
 * commande documentée pour le faire.
 *
 * LES PAGES VUES EN SPA. Ce provider ne pousse PAS de `trackPageview` sur les
 * changements de route. Le suivi des URL d'une application monopage se règle
 * côté console Contentsquare (« Project and Users → Tracking URL Changes ») ;
 * pousser en plus depuis le code doublerait chaque page vue. Si les pages
 * internes n'apparaissent pas, c'est ce réglage-là qu'il faut activer, pas ce
 * fichier qu'il faut modifier.
 *
 * LE MASQUAGE. Le tag envoie le texte des pages. Le produit authentifié affiche
 * des données clients (rapports, marques, noms) : ses coques portent
 * `data-cs-mask` (`OsShell`, `DashboardLayout`, `SharedRunPage`) ainsi que les
 * surfaces montées en portail, qui échappent à l'arbre de ces coques. La règle
 * est tenue par `contentsquare-masking.test.tsx` — un portail ajouté sans
 * masque fait échouer la suite plutôt que de fuiter en silence.
 */

/** Tag Contentsquare du projet Aikka. Public par nature, comme l'app id Intercom. */
const CONTENTSQUARE_TAG_ID = '7c211554d459f';

/**
 * En production seulement — sauf demande explicite.
 *
 * Sans cette barrière, chaque `npm run dev` et chaque aperçu enverrait des
 * sessions de développement dans le projet, où elles se mélangeraient aux vraies
 * sans moyen de les distinguer. `VITE_CONTENTSQUARE_DEV=true` lève la barrière :
 * c'est ce qu'il faut pour `npx @contentsquare/wizard verify`, qui a besoin de
 * voir le tag se charger sur `localhost` (et à qui il faut aussi accepter le
 * bandeau cookies dans le navigateur qu'il ouvre).
 */
const ENABLED =
  import.meta.env.PROD || import.meta.env.VITE_CONTENTSQUARE_DEV === 'true';

type UxaQueue = { push: (command: unknown[]) => void };

function uxa(): UxaQueue {
  const w = window as unknown as { _uxa?: unknown[] };
  w._uxa = w._uxa || [];
  return w._uxa as unknown as UxaQueue;
}

/**
 * Efface `_cs_optout`. Le cookie est posé par le tag sur le domaine courant ;
 * on tente les deux écritures (hôte exact et domaine parent avec point) parce
 * que `aikka.ai` et `www.aikka.ai` ne partagent pas la même entrée, et qu'un
 * effacement qui rate est silencieux.
 */
function clearOptout(): void {
  if (typeof document === 'undefined') return;
  const expired = 'expires=Thu, 01 Jan 1970 00:00:00 GMT; path=/';
  const host = window.location.hostname;
  const parent = host.split('.').slice(-2).join('.');
  document.cookie = `_cs_optout=; ${expired}`;
  document.cookie = `_cs_optout=; ${expired}; domain=${host}`;
  if (parent && parent !== host) {
    document.cookie = `_cs_optout=; ${expired}; domain=.${parent}`;
  }
}

/**
 * Démarre — ou arrête — le tag Contentsquare selon le consentement.
 *
 * Il ÉCOUTE le consentement plutôt que de le lire une fois, sur le patron de
 * `IntercomProvider` : accepter depuis le bandeau doit suffire, sans
 * rechargement, et retirer son accord doit couper la collecte dans la seconde.
 *
 * À placer au-dessus des routes, dans `App.tsx`.
 */
export function ContentsquareProvider({ children }: { children: React.ReactNode }) {
  const [consent, setConsent] = useState<CookieConsent | null>(() => readConsent());
  const injected = useRef(false);

  useEffect(() => subscribeConsent(setConsent), []);

  useEffect(() => {
    if (!ENABLED) return;

    if (consent?.analytics) {
      clearOptout();
      if (injected.current) return;
      injectContentsquareScript({ clientId: CONTENTSQUARE_TAG_ID });
      injected.current = true;
      return;
    }

    // Pas (ou plus) de consentement. On ne démarre rien ; et si le tag a été
    // démarré plus tôt dans cette session, `optout` arrête l'envoi et purge les
    // cookies déjà posés. Le pousser sans avoir rien démarré n'aurait aucun
    // effet utile — la file `_uxa` serait lue par un script qui ne viendra pas —
    // mais poserait `_cs_optout` chez un visiteur qui n'a jamais été suivi.
    if (injected.current) {
      uxa().push(['optout']);
    }
  }, [consent]);

  return <>{children}</>;
}
